import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheckIcon, BeakerIcon, SparklesIcon, GlobeEuropeAfricaIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

const QualityInputs = () => {
    const fadeInUp = {
        hidden: { opacity: 0, y: 40 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
    };

    const provisions = [
        "High-performance crop protection solutions",
        "Balanced & efficient fertilizers",
        "Bio-stimulants & growth enhancers",
        "Soil health improvement products",
        "Sustainable and residue-conscious formulations"
    ];

    return (
        <div className="bg-gray-50 min-h-screen font-sans">
            <section className="relative h-[60vh] min-h-[400px] flex items-center justify-center bg-gray-900 text-white overflow-hidden">
                <motion.div
                    initial={{ scale: 1.1 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 10, repeat: Infinity, repeatType: "reverse" }}
                    className="absolute inset-0 w-full h-full"
                >
                    <div className="absolute inset-0 bg-black/60 z-10"></div>
                    <img
                        src={require('../../../assets/newimages/mas wesbite input.webp')}
                        alt="Quality Inputs"
                        className="w-full h-full object-cover opacity-80"
                    />
                </motion.div>

                <div className="relative z-20 text-center px-4 max-w-5xl mx-auto">
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-4xl md:text-6xl font-bold mb-6 leading-tight"
                    >
                        Quality <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-500">Agriculture Inputs</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="text-xl md:text-2xl text-gray-200 font-light"
                    >
                        Pure Inputs. Powerful Results.
                    </motion.p>
                </div>
            </section>

            <section className="py-20 px-4 md:px-8 max-w-7xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">

                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeInUp}
                    >
                        <p className="text-gray-600 text-lg leading-relaxed mb-6 font-light">
                            At <strong className="text-gray-900 font-bold">Marvelous Agri Solution</strong>, we believe crop performance begins with input quality.
                        </p>
                        <p className="text-gray-600 text-lg leading-relaxed mb-8 font-light">
                            Our Quality Agriculture Inputs are carefully selected, scientifically validated, and performance-tested to ensure maximum efficiency, crop safety, and sustainable productivity.
                        </p>

                        <div className="bg-emerald-50 border-l-4 border-emerald-500 p-6 rounded-r-2xl mb-12">
                            <p className="text-xl font-medium text-emerald-900 italic">
                                Because strong harvests start with strong foundations.
                            </p>
                        </div>

                        <h3 className="text-3xl font-bold text-gray-900 mb-8 flex items-center gap-3">
                            What We Provide
                        </h3>

                        <div className="space-y-4 mb-8">
                            {provisions.map((item, idx) => (
                                <div key={idx} className="flex items-start gap-4 p-4 rounded-xl bg-white shadow-sm border border-gray-100 hover:border-emerald-300 hover:shadow-md transition-all">
                                    <CheckCircleIcon className="h-6 w-6 text-emerald-500 flex-shrink-0 mt-0.5" />
                                    <span className="text-gray-800 font-medium text-lg">{item}</span>
                                </div>
                            ))}
                        </div>

                        <p className="text-emerald-800 font-medium italic mb-10 border-b border-gray-200 pb-6">
                            Each product is designed to deliver consistent response, higher nutrient uptake, and visible crop improvement.
                        </p>

                    </motion.div>

                    <div className="relative">
                        <div className="sticky top-24">
                            <motion.img
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                src={require('../../../assets/newimages/mas websitre input.webp')}
                                alt="Quality Input Process"
                                className="rounded-3xl w-full h-64 object-cover shadow-xl mb-8"
                            />
                            <motion.div
                                initial={{ opacity: 0, x: 50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8 }}
                                className="bg-gray-900 p-10 rounded-3xl shadow-2xl text-white relative overflow-hidden"
                            >
                                <div className="absolute -right-20 -top-20 w-80 h-80 bg-emerald-500/20 rounded-full blur-3xl text-white"></div>

                                <h3 className="text-3xl font-bold mb-8 flex items-center gap-3 relative z-10 text-emerald-400">
                                    Why Our Inputs Stand Apart
                                </h3>

                                <div className="relative z-10 space-y-3 mb-10">
                                    <p className="text-2xl font-light text-gray-300 leading-relaxed">
                                        <span className="block line-through text-gray-500 decoration-red-500/50 decoration-2 mb-2">No compromise on purity.</span>
                                        <span className="block line-through text-gray-500 decoration-red-500/50 decoration-2">No dilution in performance.</span>
                                    </p>
                                </div>

                                <h4 className="text-xl text-emerald-300 font-bold mb-6">We focus on:</h4>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-12 relative z-10 border-b border-gray-800 pb-10">
                                    <div className="flex items-center gap-3 text-gray-300">
                                        <ShieldCheckIcon className="w-6 h-6 text-emerald-500" /> Field-tested effectiveness
                                    </div>
                                    <div className="flex items-center gap-3 text-gray-300">
                                        <BeakerIcon className="w-6 h-6 text-emerald-500" /> Crop-stage compatibility
                                    </div>
                                    <div className="flex items-center gap-3 text-gray-300">
                                        <GlobeEuropeAfricaIcon className="w-6 h-6 text-emerald-500" /> Environmental responsibility
                                    </div>
                                    <div className="flex items-center gap-3 text-gray-300">
                                        <SparklesIcon className="w-6 h-6 text-emerald-500" /> Long-term soil sustainability
                                    </div>
                                </div>

                                <div className="relative z-10">
                                    <h3 className="text-2xl font-bold mb-6 text-emerald-400 flex items-center gap-2">
                                        Built for Healthy & Wealthy Agriculture
                                    </h3>
                                    <p className="text-gray-300 mb-8 font-light text-lg">
                                        Our inputs are not just products — they are productivity enhancers.
                                    </p>

                                    <div className="flex flex-wrap gap-3 mb-10">
                                        <span className="bg-emerald-900/60 text-emerald-300 border border-emerald-500/30 px-4 py-2 rounded-full font-medium text-sm">Higher crop vigor</span>
                                        <span className="bg-emerald-900/60 text-emerald-300 border border-emerald-500/30 px-4 py-2 rounded-full font-medium text-sm">Better resistance</span>
                                        <span className="bg-emerald-900/60 text-emerald-300 border border-emerald-500/30 px-4 py-2 rounded-full font-medium text-sm">Improved yield quality</span>
                                        <span className="bg-emerald-900/60 text-emerald-300 border border-emerald-500/30 px-4 py-2 rounded-full font-medium text-sm">Stronger profitability</span>
                                    </div>

                                    <div className="flex flex-col gap-2">
                                        <p className="text-xl text-white font-bold tracking-wide">Quality You Can Trust.</p>
                                        <p className="text-xl text-white font-bold tracking-wide">Performance You Can Measure.</p>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default QualityInputs;
